import type { RouteObject } from 'react-router-dom';

import { Outlet } from 'react-router-dom';

import MarketplaceFeatureRoute from '../../allFeatures/TenantFeatureRoute/MarketplaceFeatureRoute';
import PremiumFeatureRoute from '../../allFeatures/TenantFeatureRoute/PremiumFeatureRoute';
import TenantFeatureRoute from '../../allFeatures/TenantFeatureRoute/TenantFeatureRoute';
import { lazyRoute } from './lazyRoute';
import { crudChildren } from './helpers';

/**
 * The routes of the app that a tenant can switch off (per-tenant feature
 * switches). Each is a pathless layout around the page's own route, so the
 * page keeps its path, handle and breadcrumb exactly as before — or, where the
 * route already is a layout of its own (the template pages), the wrapper is
 * that layout's element.
 *
 * Routes whose gate is a PERMISSION the page checks itself (desk, lockers)
 * need no wrapper: `hasPermission` already answers false for them while the
 * feature is off. The template pages do: their layout used to be a plain
 * `Outlet`, and the list page renders without checking a read key.
 */

/**
 * `/marketplace`: discovery of other people's listings. Off → `/`, except
 * that a checkout return (`?purchase=…`) first shows its confirmation
 * (`MarketplaceFeatureRoute`).
 */
export const marketplaceRoute = {
  children: [
    {
      lazy: () => lazyRoute('/MarketplacePage/Marketplace'),
      path: '/marketplace',
    },
  ],
  element: (
    <MarketplaceFeatureRoute>
      <Outlet />
    </MarketplaceFeatureRoute>
  ),
} satisfies RouteObject;

/**
 * `/premium`: off → `/`, unless the account keeps catalog access through its
 * subscription (`PremiumFeatureRoute`).
 */
export const premiumRoute = {
  children: [
    {
      handle: { title: 'premium' },
      lazy: () => lazyRoute('/PremiumPages/PremiumPage'),
      path: '/premium',
    },
  ],
  element: (
    <PremiumFeatureRoute>
      <Outlet />
    </PremiumFeatureRoute>
  ),
} satisfies RouteObject;

/**
 * `/booking/vip/:token`: the shared privileged-seat link is a new booking —
 * the server refuses resolving it while `sessions` is off. Off → `/`.
 *
 * The backend builds the link as `${FRONTEND_URL}${BOOKING_VIP_LINK}/<token>`
 * (`BOOKING_VIP_LINK` defaults to `/booking/vip`) — this path must stay in
 * step with that config. Org-less like `/booking-events`: the invitee arrives
 * with nothing but the token.
 */
export const bookingVipRoute = {
  children: [
    {
      handle: { title: 'bookingVip' },
      lazy: () => lazyRoute('/BookingVipPages/BookingVipPage'),
      path: '/booking/vip/:token',
    },
  ],
  element: (
    <TenantFeatureRoute feature="sessions">
      <Outlet />
    </TenantFeatureRoute>
  ),
} satisfies RouteObject;

/**
 * `organizations/:organizationId/booking-event-templates/*`: session templates
 * exist only to start new sessions. Off → `/`. Reached directly from the
 * sidebar, so no `handle.title` of its own — its form page renders its own
 * "back to Booking Event Templates" link.
 */
export const bookingEventTemplatesRoute = {
  children: crudChildren(
    '/BookingEventTemplatesPages/BookingEventTemplatesPage',
    '/BookingEventTemplatesPages/BookingEventTemplatePage',
    { hideBreadcrumbs: true },
  ),
  element: (
    <TenantFeatureRoute feature="sessions">
      <Outlet />
    </TenantFeatureRoute>
  ),
  path: 'booking-event-templates',
} satisfies RouteObject;
